import React, { createContext, useContext, useMemo } from "react";
import { T, TONE_COLOR } from "./tokens";
import { AreaConfig, DimensionKey, LifeAreasConfig, MoodConfig } from "./types";

// The Life Areas UI holds no copy of the areas, rings, bands, labels or moods:
// they come from GET /life-gps/config and are read through this context.

export type Band = { label: string; color: string };

export type Lookup = {
  config: LifeAreasConfig;
  areas: AreaConfig[]; // spec order
  area: (key: DimensionKey) => AreaConfig;
  // stored score (0-10) ↔ the scale the UI shows (0-100)
  display: (stored: number | null | undefined) => number | null;
  toStored: (shown: number) => number;
  bandFor: (shown: number | null) => Band | null;
  mood: (id?: string | null) => MoodConfig | undefined;
};

export const buildLookup = (config: LifeAreasConfig): Lookup => {
  const byKey = new Map(config.areas.map((a) => [a.key, a]));
  const { max, storageMax } = config.scale;
  const factor = max / storageMax;
  const bandsDesc = config.bands.slice().sort((a, b) => b.min - a.min);
  return {
    config,
    areas: config.areas,
    // an unknown key (e.g. a newer backend) degrades to a neutral placeholder
    area: (key) =>
      byKey.get(key) ?? {
        key,
        label: String(key),
        emoji: "•",
        color: T.color.text.muted,
        ring: "",
        shortcut: 0,
        description: "",
        subDimensions: [],
        questions: [],
        related: [],
      },
    display: (stored) => (stored == null ? null : Math.round(stored * factor)),
    toStored: (shown) => shown / factor,
    bandFor: (shown) => {
      if (shown == null || !bandsDesc.length) return null;
      const b = bandsDesc.find((x) => shown >= x.min) ?? bandsDesc[bandsDesc.length - 1];
      return { label: b.label, color: TONE_COLOR[b.tone] ?? T.color.text.secondary };
    },
    mood: (id) => (id ? config.moods.find((m) => m.id === id) : undefined),
  };
};

const Ctx = createContext<Lookup | null>(null);

export const LifeAreasProvider: React.FC<{ config: LifeAreasConfig; children: React.ReactNode }> = ({
  config,
  children,
}) => {
  const value = useMemo(() => buildLookup(config), [config]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useLifeAreas = (): Lookup => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLifeAreas must be used inside <LifeAreasProvider>");
  return v;
};
